import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Users, MapPin, FileText, Award, TrendingUp, Briefcase, Activity } from "lucide-react";
import { toast } from "sonner";
import NewsCard from "@/components/NewsCard";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  PieChart,
  Pie,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PartyDetail() {
  const { party } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [trends, setTrends] = useState(null);
  const [trendsBuilding, setTrendsBuilding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const load = () => {
      axios
        .get(`${API}/parties/${party}/trends`, {
          timeout: 30000,
          validateStatus: (s) => s === 200 || s === 202,
        })
        .then((r) => {
          if (cancelled) return;
          if (r.status === 202 || r.data?.status === "building") {
            setTrendsBuilding(true);
            timer = setTimeout(load, 12000);
            return;
          }
          setTrendsBuilding(false);
          setTrends(r.data);
        })
        .catch(() => { if (!cancelled) setTrendsBuilding(false); });
    };
    setTrends(null);
    setTrendsBuilding(false);
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [party]);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const load = () => {
      axios
        .get(`${API}/parties/${party}`, {
          timeout: 60000,
          validateStatus: (s) => s === 200 || s === 202,
        })
        .then((r) => {
          if (cancelled) return;
          if (r.status === 202 || r.data?.status === "building") {
            setBuilding(true);
            timer = setTimeout(load, 8000);
            return;
          }
          setBuilding(false);
          setData(r.data);
          setLoading(false);
        })
        .catch((e) => {
          if (cancelled) return;
          if (e?.response?.status === 404) toast.error("Party not found");
          else toast.error("Failed to load party detail");
          setLoading(false);
        });
    };
    setLoading(true);
    setBuilding(false);
    setData(null);
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [party]);

  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg)" }}>
      <Navigation />

      <header
        className="border-b border-gray-200 text-white"
        style={{ background: data ? `linear-gradient(135deg, ${data.color} 0%, #1f2937 100%)` : "#1f2937" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-3 text-white/80 hover:text-white hover:bg-white/10"
            data-testid="party-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {data && (
            <>
              <h1 className="text-4xl md:text-5xl font-bold" data-testid="party-detail-title">
                {data.displayName}
              </h1>
              <p className="text-white/80 mt-1">119th Congress · party-level aggregate</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                <HeaderStat label="Total members" value={data.stats.totalMembers} />
                <HeaderStat label="Avg. tenure" value={`${data.stats.averageYearsInOffice}y`} />
                <HeaderStat label="Bills sponsored" value={data.stats.totalBillsSponsored.toLocaleString()} />
                <HeaderStat label="Bills enacted" value={data.stats.totalBillsPassed} />
              </div>
            </>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && (
          <div className="text-center py-12" data-testid="party-loading">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3" />
            <p className="text-gray-600">
              {building
                ? "Crunching party-level stats, usually under a minute. This page will refresh automatically."
                : "Loading party aggregates…"}
            </p>
          </div>
        )}

        {!loading && data && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Membership stats column */}
            <Card className="p-6 bg-white rounded-xl border-2 border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-gray-900">Membership</h2>
              </div>
              <ChamberPie color={data.color} byChamber={data.stats.byChamber} />
              <ul className="mt-4 space-y-1 text-sm text-gray-700">
                <li className="flex justify-between"><span>House</span><span className="font-semibold">{data.stats.byChamber.House}</span></li>
                <li className="flex justify-between"><span>Senate</span><span className="font-semibold">{data.stats.byChamber.Senate}</span></li>
                <li className="flex justify-between"><span>Median tenure</span><span className="font-semibold">{data.stats.medianYearsInOffice}y</span></li>
                <li className="flex justify-between"><span>Longest tenure</span><span className="font-semibold">{data.stats.longestTenure}y</span></li>
                <li className="flex justify-between"><span>Avg bills / member</span><span className="font-semibold">{data.stats.averageBillsPerMember}</span></li>
              </ul>
            </Card>

            {/* Cohesion */}
            <Card className="p-6 bg-white rounded-xl border-2 border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-5 h-5 text-emerald-600" />
                <h2 className="text-lg font-bold text-gray-900">Voting Cohesion</h2>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Share of recorded roll-calls where ≥{data.cohesion.threshold}% of {data.displayName.toLowerCase()} voted the same way.
              </p>
              <div className="text-center py-4">
                <p className="text-6xl font-bold" style={{ color: data.color }}>
                  {data.cohesion.cohesionPct}%
                </p>
                <p className="text-xs text-gray-500 mt-1">across {data.cohesion.rollCallsAnalyzed} cached roll-call votes</p>
              </div>
            </Card>

            {/* Top policy areas */}
            <Card className="p-6 bg-white rounded-xl border-2 border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <Briefcase className="w-5 h-5 text-orange-600" />
                <h2 className="text-lg font-bold text-gray-900">Top Policy Areas Sponsored</h2>
              </div>
              {(data.topPolicyAreas || []).length === 0 ? (
                <p className="text-sm text-gray-500">Policy areas still being indexed…</p>
              ) : (
                <PolicyAreaBars items={data.topPolicyAreas} color={data.color} />
              )}
            </Card>

            {/* Geographic */}
            <Card className="p-6 bg-white rounded-xl border-2 border-gray-200 lg:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-bold text-gray-900">Geographic Distribution</h2>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.stateDistribution.slice(0, 15)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="state" stroke="#6b7280" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={60} />
                  <YAxis stroke="#6b7280" allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                    {data.stateDistribution.slice(0, 15).map((row) => (
                      <Cell key={row.state} fill={data.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* Top sponsors */}
            <MembersList title="Most Prolific Sponsors" icon={<TrendingUp className="w-5 h-5 text-blue-600" />} members={data.topSponsors} field="billsSponsored" suffix="bills" testIdPrefix="party-top-sponsor" />

            {/* Top enactors */}
            <MembersList title="Most Bills Enacted" icon={<Award className="w-5 h-5 text-emerald-600" />} members={data.topEnactors} field="billsPassed" suffix="enacted" testIdPrefix="party-top-enactor" />

            {/* Top tenured */}
            <MembersList title="Longest-Serving" icon={<Users className="w-5 h-5 text-amber-400" />} members={data.topTenured} field="yearsInOffice" suffix="years" testIdPrefix="party-top-tenured" />

            {/* Party trends across recent Congresses */}
            <Card className="p-6 bg-white rounded-xl border-2 border-gray-200 lg:col-span-3" data-testid="party-trends-card">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-gray-900">Party Trends. Recent Congresses</h2>
              </div>
              {trends ? (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    How {trends.displayName.toLowerCase()} have shifted across the {trends.congresses.map((c) => `${c.congress}th`).join(" · ")} Congresses.
                  </p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <TrendChart title="Seats held" data={trends.congresses.map((c) => ({ name: `${c.congress}th`, value: (c.seats?.House || 0) + (c.seats?.Senate || 0) }))} color={trends.color} testId="trend-seats" />
                    <TrendChart title="Voting cohesion %" data={trends.congresses.map((c) => ({ name: `${c.congress}th`, value: c.cohesionPct }))} color={trends.color} suffix="%" testId="trend-cohesion" />
                    <TrendChart title="Bills introduced" data={trends.congresses.map((c) => ({ name: `${c.congress}th`, value: c.billsSponsored }))} color={trends.color} testId="trend-sponsored" />
                    <TrendChart title="Bills enacted" data={trends.congresses.map((c) => ({ name: `${c.congress}th`, value: c.billsEnacted }))} color={trends.color} testId="trend-enacted" />
                  </div>
                  <p className="text-xs text-gray-400 mt-4">{trends.note}</p>
                </>
              ) : trendsBuilding ? (
                <div className="flex items-center gap-3 py-6 text-gray-600" data-testid="party-trends-building">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
                  <p className="text-sm">Computing multi-Congress trends, this can take a couple of minutes the first time. It will appear here automatically.</p>
                </div>
              ) : (
                <p className="text-sm text-gray-500 py-4">Trend data is not available right now.</p>
              )}
            </Card>

            {/* News, public coverage of this party */}
            <div className="lg:col-span-3">
              <NewsCard
                endpoint={`/parties/${party}/news`}
                title={`${data.displayName}. In the News`}
                limit={20}
                initialVisible={6}
                testId="party-news"
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function TrendChart({ title, data, color, suffix = "", testId }) {
  const hasData = data.some((d) => d.value != null);
  return (
    <div data-testid={testId}>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</p>
      {!hasData ? (
        <p className="text-sm text-gray-400 py-6">No data for these Congresses.</p>
      ) : (
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={data} margin={{ top: 18, right: 4, left: 4, bottom: 0 }}>
            <XAxis dataKey="name" stroke="#6b7280" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis hide />
            <Tooltip formatter={(v) => [`${v}${suffix}`, title]} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
            <Bar dataKey="value" radius={[6, 6, 0, 0]} label={{ position: "top", fontSize: 11, fill: "#9ca3af", formatter: (v) => (v == null ? "" : `${v}${suffix}`) }}>
              {data.map((row, i) => (
                <Cell key={row.name} fill={color} fillOpacity={0.45 + 0.275 * i} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function HeaderStat({ label, value }) {
  return (
    <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm border border-white/20">
      <p className="text-xs text-white/70 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

function ChamberPie({ color, byChamber }) {
  const data = [
    { name: "House", value: byChamber.House, fill: color },
    { name: "Senate", value: byChamber.Senate, fill: "#9ca3af" },
  ];
  return (
    <ResponsiveContainer width="100%" height={160}>
      <PieChart>
        <Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={70}>
          {data.map((d) => (
            <Cell key={d.name} fill={d.fill} />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  );
}

function PolicyAreaBars({ items, color }) {
  const max = Math.max(...items.map((x) => x.count));
  return (
    <ul className="space-y-2">
      {items.map((it, idx) => (
        <li key={it.name}>
          <Link
            to={`/legislation?policyArea=${encodeURIComponent(it.name)}`}
            className="block group"
            data-testid={`party-policy-area-${idx}`}
          >
            <div className="flex justify-between text-sm">
              <span className="text-gray-700 group-hover:text-orange-700 truncate">{it.name}</span>
              <span className="text-gray-500 ml-2">{it.count}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden mt-1">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${(it.count / max) * 100}%`, backgroundColor: color }}
              />
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function MembersList({ title, icon, members, field, suffix, testIdPrefix }) {
  return (
    <Card className="p-6 bg-white rounded-xl border-2 border-gray-200">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
      </div>
      <ol className="space-y-2">
        {members.map((m, idx) => (
          <li key={m.id} data-testid={`${testIdPrefix}-${idx}`}>
            <Link
              to={`/legislator/${m.id}`}
              className="flex justify-between items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-bold text-gray-400 w-5">{idx + 1}.</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{m.name}</p>
                  <p className="text-xs text-gray-500">{m.state} · {m.chamber?.replace("House of Representatives", "House")}</p>
                </div>
              </div>
              <Badge variant="outline" className="shrink-0">
                {m[field]} {suffix}
              </Badge>
            </Link>
          </li>
        ))}
      </ol>
    </Card>
  );
}
